import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import styles from "./AppointmentSlotTaken.module.scss";
import {
  AppointmentSlotTakenLocationState,
  getKnownPatientReturnTo,
} from "./locationState";
import { RootState } from "app/store";
import { Button } from "components/Button";
import * as paths from "features/routing/paths";
import { resetAppointmentBooked } from "features/scheduling/appointmentCreationSlice";
import { NeedsSelectedAppointment } from "components/NeedsSelectedAppointment";
import { SadLayout } from "features/layout/SadLayout";
import { SelectedAppointment } from "types/selectedAppointment";
import { selectVersion } from "features/appointmentDataSlice";

const message = (
  <>
    <p className={styles.message}>This appointment time has been taken.</p>
    <p className={styles.message}>Please choose another time.</p>
  </>
);

const UnconnectedAppointmentSlotTaken: React.FC<{
  knownPatientReturnTo?: string;
  selectedAppointment?: SelectedAppointment;
}> = ({ knownPatientReturnTo, selectedAppointment }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { officeId, examType, patientReturnStatus } =
    useParams<
      paths.RequiredSchedulingRouteParams<
        "officeId" | "examType" | "patientReturnStatus"
      >
    >();
  const version = useSelector((state: RootState) => selectVersion(state));

  useEffect(() => {
    return () => {
      dispatch(resetAppointmentBooked());
    };
  }, [dispatch]);

  const chooseAvailability = () => {
    if (knownPatientReturnTo) {
      history.push(knownPatientReturnTo);
      return;
    }

    if (version === 2) {
      history.push(
        paths.appointmentBookingStartV2({
          officeId,
        })
      );
    } else if (selectedAppointment) {
      history.push(
        paths.appointmentsAvailableAppointments({
          officeId,
          examType,
          patientReturnStatus,
          query: {
            date: selectedAppointment.date,
          },
        })
      );
    }
  };

  const cta = (
    <Button
      className={styles.button}
      onClick={chooseAvailability}
      size="large"
      text="Select another time"
    />
  );

  return (
    <SadLayout
      headline="We're sorry!"
      message={message}
      cta={cta}
      calledFrom="summaryPage"
      showSchedulerBanner={true}
    />
  );
};

export const AppointmentSlotTaken: React.FC = () => {
  const location = useLocation<AppointmentSlotTakenLocationState>();
  const knownPatientReturnTo = getKnownPatientReturnTo(location.state);

  if (knownPatientReturnTo) {
    return (
      <UnconnectedAppointmentSlotTaken
        knownPatientReturnTo={knownPatientReturnTo}
      />
    );
  }

  return (
    <NeedsSelectedAppointment>
      {({ selectedAppointment }) => (
        <UnconnectedAppointmentSlotTaken
          selectedAppointment={selectedAppointment}
        />
      )}
    </NeedsSelectedAppointment>
  );
};
